'use client';
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFullBleedNavScroll, useNavTopOffset } from '../../hooks';
import { NavigationBar } from './NavigationBar';
import { Header, NavWrapper, NavSpacer, BannerSpacer } from './styles';

export const BaseHeader = ({
  topSegment,
  leftSegment,
  centerSegment,
  rightSegment,
  bottomSegment,
  logoImg = null,
  logoUrl,
  logoSrText,
  sticky = false,
  stickyBanner = false,
  hasBanner,
  bgColor = 'background.dark',
  fontColor = 'text.lightest',
  showLogo = true,
  headerVerticalPadding,
  headerHorizontalPadding,
  fullBleedNav = false,
  fullBleedLogo,
  'data-testid': dataTestId,
}) => {
  const resolvedHasBanner = hasBanner ?? !!topSegment;
  const scrolled = useFullBleedNavScroll(fullBleedNav);
  // position:fixed takes the nav out of flow, so its normal-flow space must
  // be reserved separately to avoid page content rendering underneath it.
  const needsNavSpacer = sticky && !fullBleedNav;
  // The promo banner is also always position:fixed when sticky,
  // for the same reason the nav is: sticky
  // only holds within whatever slack its parent happens to have, not
  // indefinitely. Its height is a fixed 48px constant.
  const needsBannerSpacer = stickyBanner && !fullBleedNav;
  const navTopOffset = useNavTopOffset(sticky, resolvedHasBanner, stickyBanner);
  const showFullBleedLogo = Boolean(fullBleedLogo && fullBleedNav && scrolled);

  const navRef = useRef(null);
  const spacerRef = useRef(null);

  useEffect(() => {
    if (!needsNavSpacer) return undefined;
    const navEl = navRef.current;
    const spacerEl = spacerRef.current;
    if (!navEl || !spacerEl) return undefined;

    const updateHeight = () => {
      spacerEl.style.height = `${navEl.getBoundingClientRect().height}px`;
    };
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(navEl);
    return () => observer.disconnect();
  }, [needsNavSpacer]);

  return (
    <Header
      role="banner"
      data-testid={dataTestId}
      $bgColor={bgColor}
      $fontColor={fontColor}
      $fullBleedNav={fullBleedNav}
      $scrolled={scrolled}
    >
      {topSegment}
      {needsBannerSpacer && (
        <BannerSpacer data-testid="banner-spacer" aria-hidden="true" />
      )}
      <NavWrapper
        ref={navRef}
        data-testid="nav-wrapper"
        data-nav-chrome
        $sticky={sticky}
        $fullBleedNav={fullBleedNav}
        $stickyBanner={stickyBanner}
        $navTopOffset={navTopOffset}
        $bgColor={bgColor}
        $fontColor={fontColor}
        $scrolled={scrolled}
      >
        <NavigationBar
          logoImg={showFullBleedLogo ? fullBleedLogo : logoImg}
          showLogo={showLogo}
          logoUrl={logoUrl}
          logoSrText={logoSrText}
          leftSegment={leftSegment}
          centerSegment={centerSegment}
          rightSegment={rightSegment}
          bgColor={bgColor}
          fontColor={fontColor}
          verticalPadding={headerVerticalPadding}
          horizontalPadding={headerHorizontalPadding}
          fullBleedNav={fullBleedNav}
        />
      </NavWrapper>
      {needsNavSpacer && (
        <NavSpacer
          ref={spacerRef}
          data-testid="nav-spacer"
          aria-hidden="true"
          $verticalPadding={headerVerticalPadding}
        />
      )}
      {bottomSegment}
    </Header>
  );
};

BaseHeader.displayName = 'BaseHeader';

BaseHeader.propTypes = {
  logoUrl: PropTypes.string,
  logoSrText: PropTypes.string,
  topSegment: PropTypes.node,
  leftSegment: PropTypes.node,
  centerSegment: PropTypes.node,
  rightSegment: PropTypes.node,
  bottomSegment: PropTypes.node,
  sticky: PropTypes.bool,
  stickyBanner: PropTypes.bool,
  hasBanner: PropTypes.bool,
  showLogo: PropTypes.bool,
  logoImg: PropTypes.object,
  fullBleedLogo: PropTypes.object,
  headerVerticalPadding: PropTypes.number,
  headerHorizontalPadding: PropTypes.number,
  fullBleedNav: PropTypes.bool,
  'data-testid': PropTypes.string,
};
